<script setup lang="ts">
import { computed, ref, useAttrs, useSlots, watch } from 'vue'

import { IconField, InputIcon } from 'primevue'
import FloatLabel from 'primevue/floatlabel'
import PrimeSelect from 'primevue/select'

type SelectSize = 'small' | 'large'
type SelectValue = string | number | boolean | object | null

interface Props {
  modelValue?: SelectValue
  options: object[]
  label?: string
  placeholder?: string
  showClear?: boolean
  disabled?: boolean
  loading?: boolean
  editable?: boolean
  filter?: boolean
  filterPlaceholder?: string
  filterLocale?: string
  filterMatchMode?: 'startsWith' | 'contains' | 'endsWith'
  filterFields?: string[]
  resetFilterOnHide?: boolean
  checkmark?: boolean
  highlightOnSelect?: boolean
  optionLabel?: string
  optionValue?: string
  optionDisabled?: string
  optionGroupLabel?: string
  optionGroupChildren?: string
  size?: SelectSize
  scrollHeight?: string
  autoFilterFocus?: boolean
  selectOnFocus?: boolean
  focusOnHover?: boolean
  customClass?: string
  containerClass?: string
  inputClass?: string
  optionClass?: string
  filterInputClass?: string
  emptyMessageClass?: string
  errorClass?: string
  errorMessage?: string
  showErrorMessage?: boolean
  inputId?: string
  pt?: object
  ptOptions?: object
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  placeholder: '',
  showClear: true,
  disabled: false,
  loading: false,
  editable: false,
  filter: true,
  filterPlaceholder: 'Buscar...',
  filterMatchMode: 'contains',
  resetFilterOnHide: true,
  checkmark: false,
  highlightOnSelect: true,
  optionLabel: 'name',
  optionValue: 'value',
  size: 'small',
  scrollHeight: '300px',
  autoFilterFocus: true,
  selectOnFocus: false,
  focusOnHover: true,
  customClass: ' !border-white/60 dark:!border-[var(--p-primary-color)]/40 hover:!border-white dark:hover:!border-[var(--p-primary-color)]/60 dark:bg-transparent!',
  containerClass: 'w-full min-w-0 shadow-sm hover:shadow-md !rounded-lg  ',
  inputClass: 'w-full !rounded-lg !text-xs py-1! text-black-400 ',
  optionClass: '!text-xs 2xl:text-sm!  ',
  filterInputClass: '!text-xs 2xl:text-sm!',
  emptyMessageClass: '!text-xs',
  errorClass: 'text-red-400 dark:text-red-300 p-0 m-0',
  errorMessage: '',
  showErrorMessage: true
})

defineOptions({
  inheritAttrs: false
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
  'change': [event: { originalEvent: Event; value: any }]
  'focus': [event: Event]
  'blur': [event: Event]
  'before-show': []
  'before-hide': []
  'show': []
  'hide': []
  'filter': [event: { originalEvent: Event; value: string }]
}>()

const attrs = useAttrs()
const slots = useSlots()

const inputId = computed(() => props.inputId || `sel-${crypto.randomUUID()}`)

const blockKeys = [
  'modelValue', 'onUpdate:modelValue',
  'class', 'placeholder',
  'labelClass', 'label-class',
  'optionClass', 'option-class',
  'filterInputClass', 'filter-input-class',
  'emptyMessageClass', 'empty-message-class'
]

const forwardedAttrs = computed(() => {
  const src = attrs ?? {}
  return Object.fromEntries(Object.entries(src).filter(([k]) => !blockKeys.includes(k)))
})

const ptMerged = computed(() => ({
  root: { class: slots.icon ? 'pl-9' : '' },
  label: { class: 'pl-1 !text-[12px]' },
  overlay: { class: 'app-card-glass backdrop-blur-sm rounded-lg shadow-xl overflow-hidden' },
  header: { class: 'bg-transparent border-0' },
  listContainer: { class: 'bg-transparent' },
  list: { class: 'bg-transparent' },
  option: { class: optionClasses.value },
  optionLabel: { class: optionClasses.value },
  pcFilter: {
    root: { class: `${filterInputClasses.value} !bg-transparent` }
  },
  emptyMessage: { class: emptyMessageClasses.value },
  dropdown: { class: 'pr-3' },
  ...(props.pt as object || {})
}))

const containerClasses = computed(() => {
  const classAttr = attrs.class as string | undefined
  const classes = [props.containerClass, classAttr].filter(Boolean).join(' ')

  // El contenedor no es un elemento disabled, por eso eliminamos la variante
  // hover directamente cuando el Select está desactivado.
  return props.disabled
    ? classes.replace(/(^|\s)hover:!?shadow-md(?=\s|$)/g, ' ').trim()
    : classes
})

const hasError = computed(() => props.errorMessage.length > 0)

const hasSelectedValue = computed(() => {
  if (!Array.isArray(props.options) || props.options.length === 0) {
    return selectedOption.value !== undefined
  }

  return props.options.some((option) => {
    if (props.optionValue in option) {
      return (option as Record<string, unknown>)[props.optionValue] === selectedOption.value
    }

    return false
  })
})

const inputClasses = computed(() => {
  const classes = [props.inputClass, props.customClass]
  if (hasError.value) classes.push('p-invalid')
  if (hasSelectedValue.value) classes.push('p-filled')
  return classes.filter(Boolean).join(' ')
})

const labelClasses = computed(() => {
  const classAttr = attrs.labelClass || attrs['label-class']
  return [props.inputClass, props.customClass, classAttr].filter(Boolean).join(' ')
})

const optionClasses = computed(() => {
  const classAttr = attrs.optionClass || attrs['option-class']
  return [props.optionClass, classAttr].filter(Boolean).join(' ')
})

const filterInputClasses = computed(() => {
  const classAttr = attrs.filterInputClass || attrs['filter-input-class']
  return [props.filterInputClass, classAttr].filter(Boolean).join(' ')
})

const emptyMessageClasses = computed(() => {
  const classAttr = attrs.emptyMessageClass || attrs['empty-message-class']
  return [props.emptyMessageClass, classAttr].filter(Boolean).join(' ')
})

const selectedOption = ref<SelectValue>(null)
const selectRef = ref()

watch(
  () => props.modelValue,
  (newValue) => {
    selectedOption.value = newValue
  },
  { immediate: true }
)

watch(selectedOption, (newValue) => {
  emit('update:modelValue', newValue)
})

const hide = () => {
  if (selectRef.value) {
    selectRef.value.hide()
  }
}

defineExpose({
  hide
})
</script>

<template>
  <div :class="containerClasses">
    <FloatLabel variant="on" v-if="label && $slots.icon && !$slots.iconFieldRight">
      <IconField class="w-full">
        <InputIcon>
          <slot name="icon" />
        </InputIcon>
        <PrimeSelect
          ref="selectRef"
          v-bind="forwardedAttrs"
          :inputId="inputId"
          v-model="selectedOption"
          :options="options"
          :option-label="optionLabel"
          :option-value="optionValue"
          :option-disabled="optionDisabled"
          :option-group-label="optionGroupLabel"
          :option-group-children="optionGroupChildren"
          :placeholder="placeholder"
          :show-clear="showClear"
          :disabled="disabled"
          :loading="loading"
          :editable="editable"
          :filter="filter"
          :filter-placeholder="filterPlaceholder"
          :filter-locale="filterLocale"
          :filter-match-mode="filterMatchMode"
          :filter-fields="filterFields"
          :reset-filter-on-hide="resetFilterOnHide"
          :checkmark="checkmark"
          :highlight-on-select="highlightOnSelect"
          :size="size"
          :scroll-height="scrollHeight"
          :auto-filter-focus="autoFilterFocus"
          :select-on-focus="selectOnFocus"
          :focus-on-hover="focusOnHover"
          :class="inputClasses"
          :label-class="labelClasses"
          :pt="ptMerged"
          :pt-options="ptOptions"
          @change="emit('change', $event)"
          @focus="emit('focus', $event)"
          @blur="emit('blur', $event)"
          @before-show="emit('before-show')"
          @before-hide="emit('before-hide')"
          @show="emit('show')"
          @hide="emit('hide')"
          @filter="emit('filter', $event)"
        >
          <template v-if="$slots.header" #header>
            <slot name="header" />
          </template>

          <template v-if="$slots.option" #option="slotProps">
            <slot name="option" v-bind="slotProps" />
          </template>

          <template v-if="$slots.footer" #footer>
            <slot name="footer" />
          </template>

          <template v-if="$slots.empty" #empty>
            <slot name="empty" />
          </template>
        </PrimeSelect>
      </IconField>
      <label :for="inputId" class="text-xs 2xl:text-sm">{{ label }}</label>
    </FloatLabel>

    <FloatLabel variant="on" v-else-if="label && $slots.icon && $slots.iconFieldRight">
      <IconField class="w-full">
        <InputIcon>
          <slot name="icon" />
        </InputIcon>
        <PrimeSelect
          v-bind="forwardedAttrs"
          :inputId="inputId"
          v-model="selectedOption"
          :options="options"
          :option-label="optionLabel"
          :option-value="optionValue"
          :option-disabled="optionDisabled"
          :option-group-label="optionGroupLabel"
          :option-group-children="optionGroupChildren"
          :placeholder="placeholder"
          :show-clear="showClear"
          :disabled="disabled"
          :loading="loading"
          :editable="editable"
          :filter="filter"
          :filter-placeholder="filterPlaceholder"
          :filter-locale="filterLocale"
          :filter-match-mode="filterMatchMode"
          :filter-fields="filterFields"
          :reset-filter-on-hide="resetFilterOnHide"
          :checkmark="checkmark"
          :highlight-on-select="highlightOnSelect"
          :size="size"
          :scroll-height="scrollHeight"
          :auto-filter-focus="autoFilterFocus"
          :select-on-focus="selectOnFocus"
          :focus-on-hover="focusOnHover"
          :class="inputClasses"
          :label-class="labelClasses"
          :pt="ptMerged"
          :pt-options="ptOptions"
          @change="emit('change', $event)"
          @focus="emit('focus', $event)"
          @blur="emit('blur', $event)"
          @before-show="emit('before-show')"
          @before-hide="emit('before-hide')"
          @show="emit('show')"
          @hide="emit('hide')"
          @filter="emit('filter', $event)"
        >
          <template v-if="$slots.header" #header>
            <slot name="header" />
          </template>

          <template v-if="$slots.option" #option="slotProps">
            <slot name="option" v-bind="slotProps" />
          </template>

          <template v-if="$slots.footer" #footer>
            <slot name="footer" />
          </template>

          <template v-if="$slots.empty" #empty>
            <slot name="empty" />
          </template>
        </PrimeSelect>
        <InputIcon class="right-0 mr-12">
          <slot name="iconFieldRight" />
        </InputIcon>
      </IconField>
      <label :for="inputId" class="text-xs 2xl:text-sm">{{ label }}</label>
    </FloatLabel>

    <FloatLabel variant="on" v-else-if="label">
      <PrimeSelect
        v-bind="forwardedAttrs"
        :inputId="inputId"
        v-model="selectedOption"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :option-disabled="optionDisabled"
        :option-group-label="optionGroupLabel"
        :option-group-children="optionGroupChildren"
        :placeholder="placeholder"
        :show-clear="showClear"
        :disabled="disabled"
        :loading="loading"
        :editable="editable"
        :filter="filter"
        :filter-placeholder="filterPlaceholder"
        :filter-locale="filterLocale"
        :filter-match-mode="filterMatchMode"
        :filter-fields="filterFields"
        :reset-filter-on-hide="resetFilterOnHide"
        :checkmark="checkmark"
        :highlight-on-select="highlightOnSelect"
        :size="size"
        :scroll-height="scrollHeight"
        :auto-filter-focus="autoFilterFocus"
        :select-on-focus="selectOnFocus"
        :focus-on-hover="focusOnHover"
        :class="inputClasses"
        :label-class="labelClasses"
        :pt="ptMerged"
        :pt-options="ptOptions"
        @change="emit('change', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @before-show="emit('before-show')"
        @before-hide="emit('before-hide')"
        @show="emit('show')"
        @hide="emit('hide')"
        @filter="emit('filter', $event)"
      >
        <template v-if="$slots.header" #header>
          <slot name="header" />
        </template>

        <template v-if="$slots.option" #option="slotProps">
          <slot name="option" v-bind="slotProps" />
        </template>

        <template v-if="$slots.footer" #footer>
          <slot name="footer" />
        </template>

        <template v-if="$slots.empty" #empty>
          <slot name="empty" />
        </template>
      </PrimeSelect>
      <label :for="inputId" class="text-sm">{{ label }}</label>
    </FloatLabel>

    <IconField v-else-if="$slots.icon" class="w-full">
      <InputIcon>
        <slot name="icon" />
      </InputIcon>
      <PrimeSelect
        v-bind="forwardedAttrs"
        v-model="selectedOption"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :option-disabled="optionDisabled"
        :option-group-label="optionGroupLabel"
        :option-group-children="optionGroupChildren"
        :placeholder="placeholder"
        :show-clear="showClear"
        :disabled="disabled"
        :loading="loading"
        :editable="editable"
        :filter="filter"
        :filter-placeholder="filterPlaceholder"
        :filter-locale="filterLocale"
        :filter-match-mode="filterMatchMode"
        :filter-fields="filterFields"
        :reset-filter-on-hide="resetFilterOnHide"
        :checkmark="checkmark"
        :highlight-on-select="highlightOnSelect"
        :size="size"
        :scroll-height="scrollHeight"
        :auto-filter-focus="autoFilterFocus"
        :select-on-focus="selectOnFocus"
        :focus-on-hover="focusOnHover"
        :class="inputClasses"
        :label-class="labelClasses"
        :pt="ptMerged"
        :pt-options="ptOptions"
        @change="emit('change', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @before-show="emit('before-show')"
        @before-hide="emit('before-hide')"
        @show="emit('show')"
        @hide="emit('hide')"
        @filter="emit('filter', $event)"
      >
        <template v-if="$slots.header" #header>
          <slot name="header" />
        </template>

        <template v-if="$slots.option" #option="slotProps">
          <slot name="option" v-bind="slotProps" />
        </template>

        <template v-if="$slots.footer" #footer>
          <slot name="footer" />
        </template>

        <template v-if="$slots.empty" #empty>
          <slot name="empty" />
        </template>
      </PrimeSelect>
    </IconField>

    <PrimeSelect
      v-else
      v-bind="forwardedAttrs"
      v-model="selectedOption"
      :options="options"
      :option-label="optionLabel"
      :option-value="optionValue"
      :option-disabled="optionDisabled"
      :option-group-label="optionGroupLabel"
      :option-group-children="optionGroupChildren"
      :placeholder="placeholder"
      :show-clear="showClear"
      :disabled="disabled"
      :loading="loading"
      :editable="editable"
      :filter="filter"
      :filter-placeholder="filterPlaceholder"
      :filter-locale="filterLocale"
      :filter-match-mode="filterMatchMode"
      :filter-fields="filterFields"
      :reset-filter-on-hide="resetFilterOnHide"
      :checkmark="checkmark"
      :highlight-on-select="highlightOnSelect"
      :size="size"
      :scroll-height="scrollHeight"
      :auto-filter-focus="autoFilterFocus"
      :select-on-focus="selectOnFocus"
      :focus-on-hover="focusOnHover"
      :class="inputClasses"
      :label-class="labelClasses"
      :pt="ptMerged"
      :pt-options="ptOptions"
      @change="emit('change', $event)"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
      @before-show="emit('before-show')"
      @before-hide="emit('before-hide')"
      @show="emit('show')"
      @hide="emit('hide')"
      @filter="emit('filter', $event)"
    >
      <template v-if="$slots.header" #header>
        <slot name="header" />
      </template>

      <template v-if="$slots.option" #option="slotProps">
        <slot name="option" v-bind="slotProps" />
      </template>

      <template v-if="$slots.value" #value="slotProps">
        <slot name="value" v-bind="slotProps" />
      </template>

      <template v-if="$slots.footer" #footer>
        <slot name="footer" />
      </template>

      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
    </PrimeSelect>

    <div v-if="showErrorMessage && hasError" :class="errorClass">
      <small>{{ errorMessage }}</small>
    </div>
  </div>
</template>

<style scoped>
:deep(.p-floatlabel:not(:has(.p-select.p-focus)):not(:has(.p-select-open)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-400);
}

/* Placeholder */
:deep(.p-select:not(.p-focus):not(.p-select-open):not(.p-invalid) .p-select-label.p-placeholder) {
  color: var(--p-surface-400);
}

:global(html.dark .p-select:not(.p-focus):not(.p-select-open):not(.p-invalid) .p-select-label.p-placeholder) {
  color: color-mix(in srgb, var(--p-primary-50) 50%, transparent) !important;
}

:global(.dark) :deep(.p-floatlabel:not(:has(.p-select.p-focus)):not(:has(.p-select-open)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-300);
}

:global(.dark) :deep(.p-select:not(.p-focus):not(.p-select-open):not(.p-invalid) .p-select-label.p-placeholder) {
  color: var(--p-surface-300);
}

:global(.p-select-overlay.app-card-glass) {
  background: color-mix(in srgb, var(--p-surface-0) 95%, transparent) !important;
  border-color: color-mix(in srgb, var(--p-surface-0) 50%, transparent);
}

:global(.dark .p-select-overlay.app-card-glass) {
  background: color-mix(in srgb, var(--p-primary-950) 42%, transparent) !important;
}

:global(.p-select-overlay.app-card-glass .p-select-header),
:global(.p-select-overlay.app-card-glass .p-select-list-container),
:global(.p-select-overlay.app-card-glass .p-select-list) {
  background: transparent !important;
}

:global(.p-select-overlay.app-card-glass .p-select-filter) {
  background: transparent !important;
}

:global(.p-select-overlay.app-card-glass .p-select-option) {
  background: transparent !important;
}

:global(.p-select-overlay.app-card-glass .p-select-option:hover) {
  background: color-mix(in srgb, var(--p-primary-color) 18%, transparent) !important;
}

:global(.p-select-overlay.app-card-glass .p-select-option.p-focus) {
  background: color-mix(in srgb, var(--p-primary-color) 25%, transparent) !important;
}
</style>
